/**
 * ActiveCrewList - Zeigt Team-Uebersicht mit Sessions
 *
 * Anzeige:
 * - Liste aller WORK-Mitarbeiter mit ihren Sessions (RUNNING + COMPLETED)
 * - Pro Mitarbeiter: Alle Sessions mit Start-End Zeiten
 * - Abwesende (SICK/VACATION) in separater Sektion
 */

import { computed } from '@preact/signals';
import { DateTime, Interval } from 'luxon';
import { Users, Clock, UserX } from 'lucide-preact';
import { entries, hasRunningEntries } from '../../stores/timesheet.store';
import { formatMinutesAsTime } from '@timetrack/domain';
import type { TimeEntry } from '@timetrack/domain';
import styles from './ActiveCrewList.module.css';

interface EmployeeSessions {
  employeeId: string;
  employeeName: string;
  personnelNumber: string;
  sessions: TimeEntry[];
  totalMinutes: number;
  isActive: boolean; // Hat aktiven Timer
}

export function ActiveCrewList() {
  // Gruppiere WORK-Entries nach Mitarbeiter
  const employeeSessions = computed<EmployeeSessions[]>(() => {
    const workEntries = entries.value.filter((e) => e.type === 'WORK');
    const byEmployee = new Map<string, TimeEntry[]>();

    for (const entry of workEntries) {
      const existing = byEmployee.get(entry.employeeId) ?? [];
      existing.push(entry);
      byEmployee.set(entry.employeeId, existing);
    }

    const result: EmployeeSessions[] = [];
    for (const [employeeId, sessions] of byEmployee) {
      const first = sessions[0];
      if (!first) continue;

      // Sortiere nach startTime
      sessions.sort((a, b) => a.startTime.localeCompare(b.startTime));

      // Berechne Gesamtzeit (nur COMPLETED)
      let totalMinutes = 0;
      for (const session of sessions) {
        if (session.status === 'COMPLETED' && session.endTime) {
          const start = DateTime.fromISO(session.startTime);
          const end = DateTime.fromISO(session.endTime);
          const interval = Interval.fromDateTimes(start, end);
          totalMinutes += interval.length('minutes');
        }
      }

      const isActive = sessions.some((s) => s.status === 'RUNNING');

      result.push({
        employeeId,
        employeeName: first.employeeName,
        personnelNumber: first.personnelNumber,
        sessions,
        totalMinutes,
        isActive,
      });
    }

    // Aktive zuerst, dann nach Name
    return result.sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return a.employeeName.localeCompare(b.employeeName);
    });
  });

  // Abwesende
  const absentEntries = computed(() =>
    entries.value.filter((e) => e.type === 'SICK' || e.type === 'VACATION')
  );

  // Statistik
  const activeCount = computed(() =>
    employeeSessions.value.filter((e) => e.isActive).length
  );

  // Render
  return (
    <div className={styles.container}>
      {/* Team Header */}
      <header className={styles.header}>
        <Users size={20} className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>
          {hasRunningEntries.value
            ? `Team arbeitet (${activeCount.value} aktiv)`
            : `Team (${employeeSessions.value.length} Mitarbeiter)`}
        </h3>
      </header>

      {employeeSessions.value.length === 0 ? (
        <div className={styles.emptyState}>
          <p>Noch keine Mitarbeiter erfasst</p>
        </div>
      ) : (
        <div className={styles.entryList}>
          {employeeSessions.value.map((employee) => (
            <EmployeeCard key={employee.employeeId} employee={employee} />
          ))}
        </div>
      )}

      {/* Absent Entries */}
      {absentEntries.value.length > 0 && (
        <section className={styles.absentSection}>
          <div className={styles.absentHeader}>
            <UserX size={16} />
            <span>Abwesend ({absentEntries.value.length})</span>
          </div>
          <div className={styles.absentList}>
            {absentEntries.value.map((entry) => (
              <span
                key={entry.id}
                className={`${styles.absentBadge} ${
                  entry.type === 'SICK' ? styles.sick : styles.vacation
                }`}
              >
                <span className={styles.absentLabel}>
                  {entry.type === 'SICK' ? 'Krank' : 'Urlaub'}
                </span>
                {entry.employeeName}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// Employee Card - Zeigt Mitarbeiter mit allen Sessions
interface EmployeeCardProps {
  employee: EmployeeSessions;
}

function EmployeeCard({ employee }: EmployeeCardProps) {
  return (
    <div className={styles.entryCard}>
      <div className={styles.entryHeader}>
        <div className={styles.entryInfo}>
          <span className={styles.entryName}>{employee.employeeName}</span>
          <div className={styles.entryMeta}>
            <span>#{employee.personnelNumber}</span>
            {employee.totalMinutes > 0 && (
              <span className={styles.totalTime}>
                <Clock size={14} />
                {formatMinutesAsTime(Math.round(employee.totalMinutes))}
              </span>
            )}
          </div>
        </div>
        {employee.isActive ? (
          <span className={`${styles.statusBadge} ${styles.running}`}>
            <span className={styles.pulseIndicator} />
            Aktiv
          </span>
        ) : (
          <span className={`${styles.statusBadge} ${styles.completed}`}>
            Pause
          </span>
        )}
      </div>

      {/* Sessions Liste */}
      <div className={styles.sessionList}>
        {employee.sessions.map((session, idx) => (
          <SessionRow key={session.id} session={session} index={idx + 1} />
        ))}
      </div>
    </div>
  );
}

// Session Row - Einzelne Arbeitszeit-Session
interface SessionRowProps {
  session: TimeEntry;
  index: number;
}

function SessionRow({ session, index }: SessionRowProps) {
  const start = DateTime.fromISO(session.startTime);
  const end = session.endTime ? DateTime.fromISO(session.endTime) : null;

  // Dauer berechnen
  let durationText = 'läuft...';
  if (end) {
    const minutes = Interval.fromDateTimes(start, end).length('minutes');
    durationText = formatMinutesAsTime(Math.round(minutes));
  }

  return (
    <div className={`${styles.sessionRow} ${session.status === 'RUNNING' ? styles.sessionRunning : ''}`}>
      <span className={styles.sessionIndex}>{index}.</span>
      <span className={styles.sessionTime}>
        {start.toFormat('HH:mm')}
        {end ? ` - ${end.toFormat('HH:mm')}` : ' -'}
      </span>
      <span className={styles.sessionDuration}>{durationText}</span>
    </div>
  );
}
